import React from 'react'
import styled from 'styled-components'
import { IPalletDetails } from '../../../../modules/API/Pallets/models/palletDetails'
import { EXIST_PATH } from '../../../../modules/API/settings'
import ScalesIcon from '../../../icons/IconLib/ScaleIcon'
import PackageIcon from '../../../icons/IconLib/PackageBoxIcon'
import ShekelIcon from '../../../icons/IconLib/ShekelIcon'
import AppleIcon from '../../../icons/IconLib/AppleIcon'
import CardFlat from '../../../ui/card-flat/CardFlat'

type Props = {
  detail: IPalletDetails
  onRowClick: (detail: IPalletDetails) => void
}

const PalletDetailRow = ({ detail, onRowClick }: Props) => {
  const imgSrc =
    detail.GeneralProductID !== 0
      ? `${EXIST_PATH}/images/products/${detail.GeneralProductID}.png`
      : '/emptyBox.png'

  const handleClick = () => onRowClick(detail)
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') handleClick()
  }

  return (
    <RowWrapper
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-label={`${detail.ProductDesc.trim()} ${detail.VarietyDesc.trim()}`}
    >
      <ProductImage src={imgSrc} alt={detail.ProductDesc.trim()} />

      <InfoBlock>
        {/* Product name row */}
        <CardFlat.Row>
          <CardFlat.Item>
            <CardFlat.ItemIcon><AppleIcon /></CardFlat.ItemIcon>
            <CardFlat.ItemValue>
              {detail.ProductDesc.trim()} {detail.VarietyDesc.trim()} {detail.SizeID.trim()}
            </CardFlat.ItemValue>
          </CardFlat.Item>
        </CardFlat.Row>

        {/* Data row: weight / quantity / price */}
        <CardFlat.Row>
          <CardFlat.Item>
            <CardFlat.ItemIcon><ScalesIcon /></CardFlat.ItemIcon>
            <CardFlat.ItemValue>{detail.WeightNeto.toFixed(1)}</CardFlat.ItemValue>
          </CardFlat.Item>
          <CardFlat.Item>
            <CardFlat.ItemIcon><PackageIcon /></CardFlat.ItemIcon>
            <CardFlat.ItemValue>{detail.Quantity}</CardFlat.ItemValue>
          </CardFlat.Item>
          <CardFlat.Item>
            <CardFlat.ItemIcon><ShekelIcon /></CardFlat.ItemIcon>
            <CardFlat.ItemValue>{detail.SalePrice}</CardFlat.ItemValue>
          </CardFlat.Item>
        </CardFlat.Row>
      </InfoBlock>
    </RowWrapper>
  )
}

/* direction: ltr overrides the cascaded direction: rtl from CardFlat.Root
   so the image (first flex child) stays physically on the left */
const RowWrapper = styled.div`
  direction: ltr;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.8em;
  padding: 0.4em 0.3em;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.18s ease;

  &:hover {
    background: #f0f8f7;
  }

  &:focus-visible {
    outline: 2px solid #297f76;
    outline-offset: 2px;
  }
`

const ProductImage = styled.img`
  width: 3.5em;
  height: 3.5em;
  object-fit: cover;
  border-radius: 8px;
  flex-shrink: 0;
  background: #f5f5f5;
`

/* direction: rtl on InfoBlock restores RTL for the CardFlat rows inside */
const InfoBlock = styled.div`
  direction: rtl;
  display: flex;
  flex-direction: column;
  gap: 0.2em;
  flex: 1;
  min-width: 0;
`

export default PalletDetailRow
